// Google Analytics utility functions
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

// Track page views
export const trackPageView = (url: string, title?: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', 'G-BENV07HX5P', {
      page_path: url,
      page_title: title
    });
  }
};

// Track custom events
export const trackEvent = (action: string, category: string, label?: string, value?: number) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value
    });
  }
};

// Track workspace searches
export const trackWorkspaceSearch = (searchTerm: string, filters: any) => {
  trackEvent('search', 'workspace', searchTerm);
  
  // Track specific filters used
  if (filters.type) {
    trackEvent('filter_used', 'workspace_type', filters.type);
  }
  if (filters.location) {
    trackEvent('filter_used', 'location', filters.location);
  }
  if (filters.capacity) {
    trackEvent('filter_used', 'capacity', filters.capacity);
  }
};

// Track workspace views
export const trackWorkspaceView = (workspaceId: string, workspaceTitle: string, workspaceType: string) => {
  trackEvent('view_item', 'workspace', workspaceTitle);
  
  // Send to dataLayer for GTM
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'workspace_view',
      workspace_id: workspaceId,
      workspace_title: workspaceTitle,
      workspace_type: workspaceType
    });
  }
};

// Track booking attempts
export const trackBookingStart = (workspaceId: string, workspaceTitle: string, workspaceType: string) => {
  trackEvent('begin_checkout', 'booking', workspaceTitle);
  
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'booking_start',
      workspace_id: workspaceId,
      workspace_title: workspaceTitle,
      workspace_type: workspaceType
    });
  }
};

// Track successful bookings
export const trackBookingComplete = (workspaceId: string, workspaceTitle: string, workspaceType: string, leadId?: string) => {
  trackEvent('purchase', 'booking', workspaceTitle);
  
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'booking_complete',
      workspace_id: workspaceId,
      workspace_title: workspaceTitle,
      workspace_type: workspaceType,
      lead_id: leadId
    });
  }
};

// Track subscription plan views
export const trackSubscriptionPlanView = (planName: string, planPrice: number) => {
  trackEvent('view_item', 'subscription', planName, planPrice);
  
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'subscription_plan_view',
      plan_name: planName,
      plan_price: planPrice
    });
  }
};

// Track subscription purchases
export const trackSubscriptionPurchase = (planName: string, planPrice: number, duration: string) => {
  trackEvent('purchase', 'subscription', planName, planPrice);
  
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'subscription_purchase',
      plan_name: planName,
      plan_price: planPrice,
      subscription_duration: duration,
      currency: 'TRY'
    });
  }
};

// Track business partner form submissions
export const trackBusinessPartnerSubmission = (companySize: string, industry: string) => {
  trackEvent('form_submit', 'business_partner', `${companySize}_${industry}`);
  
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'business_partner_form_submit',
      company_size: companySize,
      industry: industry
    });
  }
};

// Track call request form submissions
export const trackCallRequestSubmission = (eventType: string, participantCount: string) => {
  trackEvent('form_submit', 'call_request', `${eventType}_${participantCount}`);
  
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'call_request_form_submit',
      event_type: eventType,
      participant_count: participantCount
    });
  }
};

// Track WhatsApp clicks
export const trackWhatsAppClick = (source: string) => {
  trackEvent('click', 'whatsapp', source);
  
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'whatsapp_click',
      click_source: source
    });
  }
};

// Track phone clicks
export const trackPhoneClick = (source: string) => {
  trackEvent('click', 'phone', source);
  
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'phone_click',
      click_source: source
    });
  }
};

// Track email clicks
export const trackEmailClick = (source: string) => {
  trackEvent('click', 'email', source);
  
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'email_click',
      click_source: source
    });
  }
};

// Track blog post views
export const trackBlogPostView = (postTitle: string, postCategory: string) => {
  trackEvent('view_item', 'blog_post', postTitle);
  
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'blog_post_view',
      post_title: postTitle,
      post_category: postCategory
    });
  }
};

// Track user registration
export const trackUserRegistration = (method: string = 'email') => {
  trackEvent('sign_up', 'user', method);
  
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'user_registration',
      registration_method: method
    });
  }
};

// Track user login
export const trackUserLogin = (method: string = 'email') => {
  trackEvent('login', 'user', method);
  
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'user_login',
      login_method: method
    });
  }
};